import type { FormatMode } from '@/lib/currency';

type FormatD6 = (value: string, mode?: FormatMode) => string;
type AnimatedD6Formatter = (value: number) => string;

const animatedFormatters: WeakMap<FormatD6, Map<string, AnimatedD6Formatter>> = new WeakMap<
  FormatD6,
  Map<string, AnimatedD6Formatter>
>();

/**
 * Keep AnimatedNumber's numeric tween while settling on the exact source D6
 * string. The cache prevents an unchanged formatter from restarting its tween
 * after an unrelated parent render.
 */
export function getAnimatedD6Formatter(
  formatD6: FormatD6,
  exactD6: string,
  mode: FormatMode = 'compact',
): AnimatedD6Formatter {
  const normalizedD6 = exactD6.length > 0 ? exactD6 : '0';
  let formatterByValue = animatedFormatters.get(formatD6);
  if (formatterByValue === undefined) {
    formatterByValue = new Map<string, AnimatedD6Formatter>();
    animatedFormatters.set(formatD6, formatterByValue);
  }

  const cacheKey = `${mode}:${normalizedD6}`;
  let formatter = formatterByValue.get(cacheKey);
  if (formatter === undefined) {
    const numericTarget = Number(BigInt(normalizedD6));
    formatter = (value: number): string =>
      formatD6(value === numericTarget ? normalizedD6 : String(Math.round(value)), mode);
    formatterByValue.set(cacheKey, formatter);
  }
  return formatter;
}
